import { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchUserProfile, updateUserProfile, changeUserPassword, logoutUser as logoutThunk } from '../../redux/user/profile/profileActions';
import {
  fetchPointsThunk,
  fetchUserBadgesThunk,
  fetchBadgeProgressThunk,
  fetchLeaderboardThunk
} from '../../redux/gamification/gamificationActionThunks';
import {
  fetchCredibilityThunk,
  fetchCredHistoryThunk,
  fetchCredLeaderboardThunk,
  enqueueCredRecomputeThunk
} from '../../redux/credibility/credibilityActionThunks';
import { selectOpenPredictions } from '../../redux/credibility/credibilityChipsDisplay/credibilityChipSlice';
import { selectCredLeaderboard } from '../../redux/credibility/credibilitySlice';
import { logAnalyticsEvent } from '../../redux/analytics/analyticsEventsAPI';
import { fetchProfilePicture } from '../../redux/user/profilePictures/userProfilePicturesThunks';
import { fetchWatchlistThunk } from '../../redux/user/watchlist/watchlistActions';

import AvatarUploader from '../../components/profile/ProfileAvatarSelectPicture';
import BadgeCard from '../../components/profile/badgeCardComponent';
import BadgeEarnedToast from '../../components/profile/BadgeEarnedToast';
import TierUpToast from '../../components/profile/TierUpToast';
import AnimatedNumber from '../../components/profile/AnimatedNumber';
import { getBadgeSvg } from '../../components/profile/badgeConfig';
import BuildingTrackRecordBadge from '../../components/profile/BuildingTrackRecordBadge';
import ConnectionsTab from '../../components/profile/ConnectionTab';
import StatCard from '../../components/profile/StatCard';
import InfoChip from '../../components/profile/InfoChip';
import ProgressBar from '../../components/profile/ProgressBar';
import CredibilityChipsTab from '../../components/Credibility/CredibilityChipsTab/credibilityChipsComponent';
import CredibilityExplainer from '../../components/Credibility/CredibilityExplainer';
import GuardrailTierPanel from '../../components/Credibility/GuardrailTierPanel';
import KnowledgeAssessmentSummary from '../../components/Credibility/KnowledgeAssessmentSummary';
import { InfoTooltip } from '../Dashboard/UnifiedDashboard/Tooltip';
import StockerEyeLoader from '../../components/loader/StockerEyeLoader';
import {
  User, Mail, Phone, MapPin, Calendar, Building, GraduationCap, Award, Globe, Users,
  Edit3, Save, X, Lock, LogOut, Camera, Briefcase, Star, Trophy, Target, Zap,
  Crown, Shield, Flame, TrendingUp, CheckCircle, Medal, Gift, BarChart3, Eye,
  RefreshCw, Activity, Sparkles, Timer, AlertCircle, TrendingDown
} from 'lucide-react';

// Real backend tier order (credibility_service.py's derive_status_tier),
// lowest to highest -- used to confirm a tier change is genuinely an
// increase before celebrating it.
const CREDIBILITY_TIER_ORDER = ['Beginner', 'Active', 'Advanced', 'Elite', 'Top Emperor'];

/* ---------------------- ADAPTERS (normalize API shapes) ---------------------- */

const first = (...vals) => vals.find(v => v !== undefined && v !== null && v !== '') ?? undefined;

const adaptPoints = (raw) => {
  if (!raw || typeof raw !== 'object') return { total: 0, streak: 0, daily: 0 };
  return {
    total: Number(first(raw.total_points, raw.points, 0)) || 0,
    streak: Number(first(raw.streak, raw.current_streak, 0)) || 0,
    daily: Number(first(raw.daily_points, 0)) || 0,
  };
};

const adaptBadge = (b, t) => {
  if (!b || typeof b !== 'object') return null;

  try {
    const nested = b.badge || b.meta || b.metadata || {};
    const name = first(
      b.badge_name,
      b.name,
      b.title,
      nested.badge_name,
      nested.name,
      nested.title,
      t ? t('myAccount.fields.unknownBadgeName') : 'Unknown Badge'
    );
    
    const rawId = first(b.badge_id, b.id, nested.id, name);
    if (!rawId) return null;

    // Create consistent slug format
    const slugId = rawId.toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

    return {
      id: slugId,
      name: String(name),
      description: String(first(
        b.description, 
        b.badge_description, 
        nested.description, 
        nested.badge_description, 
        ''
      )),
      points: Number(first(
        b.points_reward, 
        b.points, 
        nested.points_reward, 
        nested.points, 
        0
      )) || 0,
      progress: Math.max(0, Number(first(
        b.current_value, 
        b.progress?.current, 
        b.progress, 
        0
      )) || 0),
      total: Math.max(1, Number(first(
        b.required_value, 
        b.progress?.required, 
        b.total, 
        1
      )) || 1),
      rarity: String(first(b.rarity, b.tier, nested.rarity, nested.tier, 'common')),
      icon: getBadgeSvg({id: slugId,name: String(name)}) || String(first(b.icon, b.icon_name, nested.icon, nested.icon_name, 'award')),
      earnedAt: first(b.earned_at, b.created_at, null),
    };
  } catch (error) {
    console.warn('Error adapting badge:', error, b);
    return null;
  }
};

const adaptCredibility = (c) => {
  if (!c || typeof c !== 'object') return null;

  const d = c.data || {};

  return {
    score: Number(first(d.global_score, d.overall_score, 0)) || 0,
    tier: String(first(d.status_tier, d.rank, 'N/A')),
    accuracyPct: Number(first(
      d.prediction_accuracy_pct, 
      d.prediction_accuracy, 
      0
    )) || 0,

    // ✅ New metrics
    totalPredictions: Number(first(d.total_predictions, 0)) || 0,
    correctPredictions: Number(first(d.correct_predictions, 0)) || 0,
    combinedAccuracy: Number(first(d.combined_accuracy, 0)) || 0,
    hardBonus: Number(first(d.hard_prediction_bonus, 0)) || 0,

    // 2026-07-25: hard/soft prediction-quality weighting disclosure -- how
    // many of this user's resolved predictions were hard (real target,
    // magnitude-verified, weighted 1.0x toward accuracy/confidence) vs soft
    // (direction-only, weighted 0.4x). See CredibilityExplainer.jsx.
    hardPredictionsTotal: Number(first(d.hard_predictions_total, 0)) || 0,
    softPredictionsTotal: Number(first(d.soft_predictions_total, 0)) || 0,
    hardPredictionWeight: Number(first(d.hard_prediction_weight, 1.0)) || 1.0,
    softPredictionWeight: Number(first(d.soft_prediction_weight, 0.4)) || 0.4,

    consistency: Number(first(d.consistency_bonus, d.activity_score, 0)) || 0,
    influence: Number(first(d.influence_bonus, 0)) || 0,
    // Real CredibilityResponse fields the frontend never read before this
    // pass, despite the backend always returning them.
    baseAccuracy: Number(first(d.base_accuracy, 0)) || 0,
    evidenceBonus: Number(first(d.evidence_bonus, 0)) || 0,
    transparency: String(first(d.transparency_level, '—')),
    status: String(c.status || 'ready'),
    modelVersion: d.model_version || null,
    calculatedAt: d.calculated_at || null,
  };
};


const adaptLeaderboardRow = (p) => {
  if (!p || typeof p !== 'object') return null;
  
  return {
    userId: first(p.user_id, p.id),
    username: String(first(p.username, p.full_name, `User ${first(p.user_id, p.id) || ''}`)),
    total: Number(first(p.total_points, p.points, 0)) || 0,
    badgesCount: Number(first(p.badges_count, p.badge_count, 0)) || 0,
  };
};

const adaptCredLeaderboardRow = (c) => {
  if (!c || typeof c !== 'object') return null;
  return {
    userId: c.user_id,
    username: String(first(c.username, `User ${c.user_id ?? ''}`)),
    score: Number(c.global_score) || 0,
    tier: c.status_tier || 'Beginner',
    accuracy: Number(first(c.combined_accuracy, c.base_accuracy, 0)) || 0,
  };
};

const adaptWatchlist = (w) => {
  if (!w) return { items: [], total: 0 };
  
  const items = Array.isArray(w.items) ? w.items : Array.isArray(w) ? w : [];
  return {
    items,
    total: Number(first(w.total, items.length, 0)) || 0,
  };
};

/* ----------------------- Helper functions for forms ------------------------- */

const looksLikeJSON = (s) => typeof s === 'string' && /^[\[\{]/.test(s.trim());
const safeParse = (s) => { 
  try { 
    return JSON.parse(s); 
  } catch { 
    return null; 
  } 
};

const toDisplay = (v) => {
  if (!v) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') {
    return Object.entries(v)
      .map(([k, val]) => `${k}=${val ?? ''}`)
      .join('\n');
  }
  return String(v);
};

const toArray = (s) => {
  if (!s) return null;
  if (looksLikeJSON(s)) return safeParse(s) || null;
  return s.split(',').map(t => t.trim()).filter(Boolean);
};

const toObject = (s) => {
  if (!s) return {};
  if (looksLikeJSON(s)) return safeParse(s) || {};
  
  const out = {};
  s.split(/\r?\n|,/)
    .map(x => x.trim())
    .filter(Boolean)
    .forEach(pair => {
      const idx = pair.indexOf('=');
      if (idx > 0) {
        const k = pair.slice(0, idx).trim();
        const v = pair.slice(idx + 1).trim();
        if (k) out[k] = v;
      }
    });
  return out;
};

/* --------------------------------- UI -------------------------------------- */

const ProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux selectors with safe defaults
  const { loading: profileLoading = false, user, error: profileError, saving = false, passwordChanging = false } =
    useSelector((s) => s.profile || {});
  const userId = user?.id;

  const { profilePictures = {} } = useSelector((s) => s.profilePictures || {});
  const avatarUrl = userId ? profilePictures[userId] ?? null : null;

  const openPredictions = useSelector((state) =>
    selectOpenPredictions(state, user?.id)
  );

  const gamificationState = useSelector((s) => s.gamification || {});
  const {
    points: rawPoints = {},
    badges: rawBadges = {},
    badgeProgress: rawBadgeProgress = {},
    leaderboard: rawLeaderboard = [],
    loading: gamificationLoading = {},
    error: gamificationError,
  } = gamificationState;

  const credState = useSelector(
    (s) => s.credibility || { byUser: {}, loading: false }
  );
  const rawCredLeaderboard = useSelector(selectCredLeaderboard);
  const rawCredHistory = useSelector((s) =>
    userId ? s.credibility?.historyByUser?.[userId] : null
  );
  const wlSel = useSelector((s) => s.watchlist || {});

  // Local UI state
  const [editMode, setEditMode] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [activeTab, setActiveTab] = useState("chips");
  // AppLayout.jsx's <main overflow-y-auto> is this app's one real scroll
  // container (the window/body itself never scrolls -- see that file's own
  // "ONE scroll authority" comment), so window.scrollY doesn't apply here.
  // The 5 profile tabs render wildly different content heights (a large data
  // table vs. a short settings form) AND each tab's data (credibility,
  // achievements, connections) loads in asynchronously after the initial
  // render, growing the container taller over the following ~1s and pushing
  // scrollTop around even after a single one-time restore. Re-assert the
  // saved position on every frame for a short window after switching, so
  // late-arriving content can't drag it away from where the user was.
  const scrollPositionRef = useRef(0);
  const getScrollContainer = () => document.querySelector('main');
  const handleTabChange = useCallback((tabId) => {
    scrollPositionRef.current = getScrollContainer()?.scrollTop ?? 0;
    setActiveTab(tabId);
  }, []);
  useLayoutEffect(() => {
    const container = getScrollContainer();
    if (!container) return;
    container.scrollTop = scrollPositionRef.current;

    // Stop pinning the moment the user actually tries to scroll themselves --
    // 'wheel'/'touchstart' are real scroll intent; the plain 'scroll' event
    // also fires from our own assignment above, so it can't be used here.
    let cancelled = false;
    const stopPinning = () => { cancelled = true; };
    container.addEventListener('wheel', stopPinning, { passive: true, once: true });
    container.addEventListener('touchstart', stopPinning, { passive: true, once: true });

    let frame;
    const start = performance.now();
    const pin = (now) => {
      if (cancelled) return;
      container.scrollTop = scrollPositionRef.current;
      if (now - start < 800) frame = requestAnimationFrame(pin);
    };
    frame = requestAnimationFrame(pin);

    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('wheel', stopPinning);
      container.removeEventListener('touchstart', stopPinning);
    };
  }, [activeTab]);
  const [isPolling, setIsPolling] = useState(false);

  const [profileData, setProfileData] = useState({
    email: "",
    username: "",
    bio: "",
    phone_number: "",
    address: "",
    date_of_birth: "",
    backup_email: "",
    full_name: "",
    location: "",
    professional_title: "",
    company: "",
    education: "",
    skills: "",
    experience: "",
    website: "",
    social_media_links: "",
    interests: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
  });

  /* ----------------- Derived / memoized state ----------------- */
  const points = useMemo(() => {
    return userId && rawPoints[userId]
      ? adaptPoints(rawPoints[userId])
      : adaptPoints(null);
  }, [userId, rawPoints]);

  const earnedBadges = useMemo(() => {
    if (!userId || !rawBadges[userId]) return [];
    try {
      return Array.isArray(rawBadges[userId])
        ? rawBadges[userId].map((b) => adaptBadge(b, t)).filter(Boolean)
        : [];
    } catch (err) {
      console.warn("Error processing earned badges:", err);
      return [];
    }
  }, [userId, rawBadges, t]);

  // Badge acquisition is otherwise completely silent (no toast/modal exists
  // anywhere) -- detect newly-earned badges by diffing against the last-seen
  // set persisted per user, and surface the one real acknowledgment moment
  // this system has. Seeds silently on first-ever load so existing badges
  // don't all "unlock" at once the first time this ships.
  const [newBadgeToast, setNewBadgeToast] = useState(null);
  useEffect(() => {
    if (!userId || earnedBadges.length === 0) return;
    const storageKey = `stocker_seen_badges_${userId}`;
    let seenIds;
    try {
      seenIds = new Set(JSON.parse(localStorage.getItem(storageKey) || '[]'));
    } catch {
      seenIds = new Set();
    }
    const isFirstLoad = seenIds.size === 0 && !localStorage.getItem(`${storageKey}_seeded`);
    const newlyEarned = earnedBadges.find((b) => b.id && !seenIds.has(b.id));

    if (newlyEarned && !isFirstLoad) {
      setNewBadgeToast(newlyEarned);
    }

    const updatedIds = earnedBadges.map((b) => b.id).filter(Boolean);
    localStorage.setItem(storageKey, JSON.stringify(updatedIds));
    localStorage.setItem(`${storageKey}_seeded`, '1');
  }, [userId, earnedBadges]);

  const progressBadges = useMemo(() => {
    if (!userId) return [];
    try {
      const rawProg = rawBadgeProgress[userId];
      if (!rawProg) return [];
      const arr = Array.isArray(rawProg?.badges)
        ? rawProg.badges
        : Array.isArray(rawProg)
        ? rawProg
        : [];
      // This endpoint returns EVERY badge (earned and unearned) so the "in
      // progress" bar can be computed -- but adaptBadge doesn't carry the
      // is_earned flag through, and this list is rendered unconditionally
      // as "Available Achievements" with isEarned={false}. Without this
      // filter, an already-earned badge (e.g. "First Post") shows up a
      // second time here, locked, with its live (now past-threshold)
      // current_value -- literally "26 / 1, 100% complete, still locked".
      return arr
        .filter((b) => !(b?.is_earned ?? b?.earned ?? false))
        .map((b) => adaptBadge(b, t))
        .filter(Boolean);
    } catch (err) {
      console.warn("Error processing progress badges:", err);
      return [];
    }
  }, [userId, rawBadgeProgress, t]);

  const credibility = useMemo(() => {
    return userId && credState.byUser[userId]
      ? adaptCredibility(credState.byUser[userId])
      : null;
  }, [userId, credState]);

  const credibilityStatus = credibility?.status || credState?.status || null;

  // Credibility tier changes (Beginner -> Active -> Advanced -> Elite ->
  // Top Emperor) have no celebration anywhere in the app -- diff
  // against the last-seen tier per user, the same localStorage-diff
  // pattern already used for badges above, gated to only fire on a real
  // *increase* (never on a drop) and seeded silently on first-ever load so
  // an existing tier doesn't "unlock" the moment this ships.
  const [tierUpToast, setTierUpToast] = useState(null);
  useEffect(() => {
    if (!userId || !credibility?.tier) return;
    const storageKey = `stocker_seen_tier_${userId}`;
    const previousTier = localStorage.getItem(storageKey);
    const isFirstLoad = !previousTier;

    if (
      !isFirstLoad &&
      previousTier !== credibility.tier &&
      CREDIBILITY_TIER_ORDER.indexOf(credibility.tier) > CREDIBILITY_TIER_ORDER.indexOf(previousTier)
    ) {
      setTierUpToast(credibility.tier);
    }
    localStorage.setItem(storageKey, credibility.tier);
  }, [userId, credibility?.tier]);

  // The Overall Score number used to just snap to its new value the moment
  // a recompute finished -- no payoff for the "Analyzing..." wait. Detect
  // the specific pending-202 -> resolved transition (a fresh calculation
  // genuinely just landed) and animate only that reveal, not ordinary
  // mounts/re-renders where the score hasn't actually changed.
  const prevCredStatusRef = useRef(credibilityStatus);
  const [justCalculated, setJustCalculated] = useState(false);
  useEffect(() => {
    const prevStatus = prevCredStatusRef.current;
    prevCredStatusRef.current = credibilityStatus;
    if (prevStatus === 'pending-202' && credibilityStatus && credibilityStatus !== 'pending-202') {
      setJustCalculated(true);
      const t = setTimeout(() => setJustCalculated(false), 1200);
      return () => clearTimeout(t);
    }
  }, [credibilityStatus]);

  const leaderboard = useMemo(() => {
    try {
      // The real GET /gamification/leaderboard response is
      // { users: [...], total_users, last_updated } -- not a bare array --
      // so Array.isArray(rawLeaderboard) was always false and this section
      // silently rendered nothing regardless of how much real leaderboard
      // data existed server-side.
      const rows = Array.isArray(rawLeaderboard)
        ? rawLeaderboard
        : Array.isArray(rawLeaderboard?.users)
        ? rawLeaderboard.users
        : [];
      return rows.map(adaptLeaderboardRow).filter(Boolean);
    } catch (err) {
      console.warn("Error processing leaderboard:", err);
      return [];
    }
  }, [rawLeaderboard]);

  // The real, accuracy-ranked leaderboard (GET /credibility/leaderboard/top,
  // ordered by global_score) -- previously fetched-and-stored by the redux
  // slice but never dispatched or rendered anywhere, so the only leaderboard
  // a user ever saw was the activity/points one above.
  const credLeaderboard = useMemo(() => {
    try {
      return (Array.isArray(rawCredLeaderboard) ? rawCredLeaderboard : [])
        .map(adaptCredLeaderboardRow)
        .filter(Boolean);
    } catch (err) {
      console.warn("Error processing credibility leaderboard:", err);
      return [];
    }
  }, [rawCredLeaderboard]);

  // Score-over-time snapshots (GET /credibility/{user_id}/history) -- fetched
  // by fetchCredHistoryThunk but previously never rendered anywhere, so the
  // "why did my score change" trajectory was invisible even though the data existed.
  const credHistory = useMemo(() => {
    try {
      const rows = Array.isArray(rawCredHistory) ? rawCredHistory : [];
      return rows
        .map((r) => ({ score: Number(r.global_score) || 0, at: r.snapshot_at }))
        .filter((r) => r.at)
        .sort((a, b) => new Date(a.at) - new Date(b.at));
    } catch (err) {
      console.warn("Error processing credibility history:", err);
      return [];
    }
  }, [rawCredHistory]);

  const watchlist = useMemo(() => {
    try {
      return adaptWatchlist(wlSel);
    } catch (err) {
      console.warn("Error processing watchlist:", err);
      return { items: [], total: 0 };
    }
  }, [wlSel]);

  const watchlistItems = watchlist.items;
  const watchlistTotal = watchlist.total;

  const loading =
    profileLoading ||
    gamificationLoading.points ||
    gamificationLoading.badges ||
    credState.loading;

  /* ----------------- Effects ----------------- */

  // Load profile once -- guarded the same way securitySettingsPage.jsx's
  // already-fixed MFA bug was. GlobalBar's dropDown.jsx independently
  // fetches the same profile on every authenticated page; without this
  // guard the two compounded into 7+ near-simultaneous GET /users/me calls
  // on a single page load, confirmed live via network trace. See
  // Technical_Docs/13_FIX_HISTORY.md's Frontend Completeness audit pass.
  useEffect(() => {
    if (!user) dispatch(fetchUserProfile());
  }, [dispatch, user]);

  // Load related data when userId changes
  useEffect(() => {
    if (!userId) return;
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(fetchPointsThunk(userId)),
          dispatch(fetchUserBadgesThunk(userId)),
          dispatch(fetchBadgeProgressThunk(userId)),
          dispatch(fetchCredibilityThunk({ userId, soft: true })),
          dispatch(fetchCredHistoryThunk({ userId, limit: 50 })),
          dispatch(fetchCredLeaderboardThunk({ limit: 10 })),
          dispatch(fetchWatchlistThunk()),
          dispatch(fetchLeaderboardThunk(10)),
        ]);
        if (!profilePictures[userId]) {
          dispatch(fetchProfilePicture(userId));
        }
      } catch (err) {
        console.warn("Error loading profile data:", err);
      }
    };
    loadData();
    // ✅ only depend on profilePictures[userId] to avoid loops
  }, [dispatch, userId, profilePictures[userId]]);

  // Poll credibility if in pending state
  useEffect(() => {
    if (credibilityStatus === "pending-202" && userId && !isPolling) {
      setIsPolling(true);
      const interval = setInterval(() => {
        dispatch(fetchCredibilityThunk({ userId, soft: true }));
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [credibilityStatus, userId, dispatch, isPolling]);

  // Hydrate editable profile form
  useEffect(() => {
    if (!user || editMode) return;
    try {
      setProfileData({
        email: user.email || "",
        username: user.username || "",
        bio: user.bio || "",
        phone_number: user.phone_number || "",
        address: user.address || "",
        date_of_birth: user.date_of_birth || "",
        backup_email: user.backup_email || "",
        full_name: user.full_name || "",
        location: user.location || "",
        professional_title: user.professional_title || "",
        company: user.company || "",
        education: toDisplay(user.education),
        skills: toDisplay(user.skills),
        experience: toDisplay(user.experience),
        website: user.website || "",
        social_media_links: toDisplay(user.social_media_links),
        interests: toDisplay(user.interests),
      });
    } catch (err) {
      console.warn("Error hydrating profile data:", err);
    }
  }, [user, editMode]);

  // Auto-refresh core data every 30 minutes + when tab becomes visible
  useEffect(() => {
    if (!userId) return;
    const refreshData = () => {
      if (document.visibilityState !== "visible") return;
      try {
        dispatch(fetchPointsThunk(userId));
        dispatch(fetchUserBadgesThunk(userId));
        dispatch(fetchBadgeProgressThunk(userId));
        dispatch(fetchCredibilityThunk({ userId, soft: true }));
        dispatch(fetchCredHistoryThunk({ userId, limit: 50 }));
        dispatch(fetchCredLeaderboardThunk({ limit: 10 }));
        dispatch(fetchWatchlistThunk());
        dispatch(fetchLeaderboardThunk(10));
        if (!avatarUrl) {
          dispatch(fetchProfilePicture(userId));
        }
      } catch (err) {
        console.warn("Error refreshing data:", err);
      }
    };
    const intervalId = setInterval(refreshData, 30 * 60 * 1000);
    document.addEventListener("visibilitychange", refreshData);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshData);
    };
    // ✅ removed avatarUrl from deps to prevent loops
  }, [dispatch, userId]);

  /* ----------------- Handlers ----------------- */
  const handleCredibilityRecompute = useCallback(() => {
    if (userId) {
      dispatch(enqueueCredRecomputeThunk({ userId }));
    }
  }, [dispatch, userId]);

  const handleProfileChange = useCallback((e) => {
    setProfileData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPasswordData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  // Was dispatch-and-forget: dispatch() itself never throws, so the try/catch
  // was dead code and the form closed unconditionally, before the request
  // resolved -- a failed save told the user "done" anyway. profileReducer.js
  // already tracks `saving`/`error` correctly; this just wasn't using them.
  const handleProfileSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        const formattedData = {
          ...profileData,
          education: toArray(profileData.education),
          skills: toArray(profileData.skills),
          experience: toArray(profileData.experience),
          interests: toArray(profileData.interests),
          social_media_links: toObject(profileData.social_media_links),
        };
        await dispatch(updateUserProfile(formattedData)).unwrap();
        setEditMode(false);
        setActiveSection(null);
      } catch (err) {
        console.warn("Error updating profile:", err);
      }
    },
    [dispatch, profileData]
  );

  const handlePasswordSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        await dispatch(changeUserPassword(passwordData)).unwrap();
        setPasswordData({ currentPassword: "", newPassword: "" });
        setActiveSection(null);
      } catch (err) {
        console.warn("Error changing password:", err);
      }
    },
    [dispatch, passwordData]
  );

  const handleLogout = useCallback(() => {
    dispatch(logoutThunk());
  }, [dispatch]);


  /* ----------------------------- Icon helpers ------------------------------ */

  const getFieldIcon = (field) => {
    const icons = {
      email: Mail, 
      phone_number: Phone, 
      location: MapPin, 
      address: MapPin, 
      date_of_birth: Calendar,
      company: Building, 
      professional_title: Briefcase, 
      education: GraduationCap, 
      skills: Award,
      website: Globe, 
      interests: Star
    };
    return icons[field] || User;
  };

  const formatFieldName = (field) => {
    return t(`myAccount.fields.${field}`, {
      defaultValue: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    });
  };

  /* ------------------------------ Subcomponents ---------------------------- */

  const ProfileHeader = () => (
  <div className="relative bg-cream-100 dark:bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-cream-300 dark:border-white/10 overflow-visible mb-8">
    {/* Professional Finance Background Pattern -- 2026-09-11: this layer sits
        absolute inset-0 over the whole card and used an unconditional dark
        navy gradient (from-slate-900, no dark: prefix) that fully covered
        the light-mode cream-100 wrapper beneath it. Its white-dot pattern
        and colored blur accents were tuned for a dark backdrop too, so the
        gradient itself is scoped dark:-only rather than trying to build a
        separate light-mode decorative treatment. */}
    <div className="absolute inset-0 dark:bg-gradient-to-br dark:from-slate-900 overflow-hidden">
      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 opacity-10"
           style={{
             backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
             backgroundSize: '32px 32px'
           }}>
      </div>

      {/* Financial chart-inspired decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
        <div className="absolute top-8 right-8 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-8 left-8 w-24 h-24 bg-gradient-to-br from-emerald-400/10 to-teal-400/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-gradient-to-br from-violet-400/10 to-purple-400/10 rounded-full blur-lg"></div>
      </div>

      {/* Subtle line chart pattern */}
      <svg className="absolute bottom-0 right-0 w-full max-w-96 h-32 opacity-5" viewBox="0 0 400 120" fill="none" preserveAspectRatio="xMaxYMax meet">
        <path d="M0,60 Q100,20 200,40 T400,30" stroke="currentColor" strokeWidth="2" fill="none" className="text-blue-300"/>
        <path d="M0,80 Q100,40 200,60 T400,50" stroke="currentColor" strokeWidth="2" fill="none" className="text-emerald-300"/>
      </svg>
    </div>

    {/* Content Container with proper responsive spacing */}
    <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
      
      {/* Main Profile Section */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8 xl:gap-12">
        
        {/* Avatar Section - Fixed positioning to prevent overlap */}
        <div className="flex-shrink-0 self-center lg:self-start">
          <div className="relative">
            <AvatarUploader 
              userId={user?.id} 
              className="w-20 h-20 sm:w-28 sm:h-28 lg:w-32 lg:h-32 xl:w-36 xl:h-36"
            />
            {points.total > 0 && (
              <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg border-2 border-cream-300 dark:border-white/20 backdrop-blur-sm">
                {t('myAccount.levelBadge', { level: Math.floor(points.total / 100) + 1 })}
              </div>
            )}
          </div>
        </div>

        {/* Profile Info Section - Flexible with proper constraints */}
        <div className="flex-1 min-w-0 space-y-4 sm:space-y-6">
          
          {/* Name and Title Row */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 dark:text-white truncate">
                {/* 2026-09-11: bg-clip-text + text-transparent makes the outer
                    h1's own color irrelevant -- only this gradient renders,
                    and it was white/pale-blue/pale-slate only, invisible
                    against a light background. Added a dark, saturated
                    light-mode gradient so the same "gradient text" flourish
                    reads on both themes instead of just dark mode. */}
                <span className="bg-gradient-to-r from-gray-900 via-blue-900 to-slate-700 dark:from-white dark:via-blue-100 dark:to-slate-200 bg-clip-text text-transparent">
                  {profileData.full_name || profileData.username || t('myAccount.defaultName')}
                </span>
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
                <p className="text-base sm:text-lg text-gray-600 dark:text-slate-300 truncate">
                  {profileData.professional_title || t('myAccount.defaultTitle')}
                </p>
                {profileData.company && (
                  <>
                    <span className="hidden sm:inline text-gray-500 dark:text-slate-500">•</span>
                    <p className="text-sm sm:text-base text-gray-500 dark:text-slate-400 truncate">
                      {profileData.company}
                    </p>
                  </>
                )}
              </div>
            </div>
            
            {/* Streak Badge - Responsive positioning */}
            {points.streak > 7 && (
              <div className="flex items-center bg-gradient-to-r from-orange-500/90 to-red-500/90 backdrop-blur-sm text-white px-3 py-2 rounded-full text-sm font-semibold shadow-lg border border-cream-300 dark:border-white/10 flex-shrink-0">
                <Flame className="w-4 h-4 mr-2" />
                <span className="whitespace-nowrap">{t('myAccount.streakDays', { count: points.streak })}</span>
              </div>
            )}
          </div>

          {/* Stats Grid - Responsive layout */}
          <div className="bg-cream-100 dark:bg-white/5 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-cream-300 dark:border-white/10">
            {/* 2026-08-11: sm:grid-cols-4 locked in 4-up as early as 640px --
                this card sits beside a persistent sidebar, so its real
                content width at that viewport is much narrower than 4
                columns need (confirmed live: labels overlapping into
                neighboring cells). First widened to md: (768px), which
                stopped the overlap but still squeezed labels down to a
                single truncated character around 800px (confirmed live via
                screenshot) -- lg: (1024px) is where there's actually enough
                room for real label text, not just non-overlapping text. */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
              <StatCard
                value={points.total}
                label={t('myAccount.stats.portfolioXp')}
                color="text-blue-700 dark:text-blue-300"
                className="text-center"
              />
              <StatCard
                value={earnedBadges.length}
                label={t('myAccount.stats.achievements')}
                color="text-emerald-700 dark:text-emerald-300"
                className="text-center"
              />
              <StatCard
                value={points.streak}
                label={t('myAccount.stats.activeDays')}
                color="text-violet-700 dark:text-violet-300"
                className="text-center"
              />
              <StatCard
                value={openPredictions.length}
                label={t('myAccount.stats.openPredictions')}
                color="text-amber-700 dark:text-amber-300"
                className="text-center"
              />
            </div>

            {/* Level Progress Bar */}
            <ProgressBar
              value={points.total % 100}
              max={100}
              label={t('myAccount.levelProgressLabel')}
              className="mt-4"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              {t('myAccount.activityDisclaimer')}
            </p>
          </div>

          {/* Credibility Section - Enhanced for finance platform */}
          {credibility && (
            <div className="bg-gradient-to-r from-white/5 to-blue-500/5 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-cream-300 dark:border-white/10 shadow-lg">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                  <Shield className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-blue-400" />
                  {t('myAccount.credibility.title')}
                </h3>
                <div className="flex items-center gap-2">
                  <BuildingTrackRecordBadge totalPredictions={credibility.totalPredictions} />
                  {credibilityStatus === 'pending-202' && (
                    <div className="flex items-center text-amber-700 dark:text-amber-400 text-sm bg-amber-100 dark:bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                      <Timer className="w-4 h-4 mr-2 animate-spin" />
                      <span className="whitespace-nowrap">{t('myAccount.credibility.analyzing')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Grid of Metrics. lg: -> xl: (2026-08-11): same real-content-
                  width-vs-viewport-width mismatch as the stats grid above --
                  this card sits beside a persistent sidebar, so 4-up needs
                  more room than lg's 1024px gave it. min-w-0 on each cell +
                  truncate on its label is this project's own established
                  fix for this overlap shape (13_FIX_HISTORY.md, fifty-third
                  pass). */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                {/* Overall Score */}
                <div className={`min-w-0 bg-cream-100 dark:bg-white/5 rounded-xl p-4 text-center sm:text-left border border-cream-300 dark:border-white/10 hover:bg-cream-900/10 dark:hover:bg-white/10 transition ${justCalculated ? 'animate-pop' : ''}`}>
                  <div className="text-3xl font-bold text-blue-400">
                    <AnimatedNumber value={credibility.score} decimals={1} animate={justCalculated} />
                  </div>
                  <div className="text-sm text-gray-500 dark:text-slate-400 flex items-center justify-center sm:justify-start gap-1.5 min-w-0">
                    <span className="truncate">{t('myAccount.credibility.overallScore')}</span>
                    <InfoTooltip content={t('myAccount.credibility.overallScoreTooltip')} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-500 mt-1 truncate">{credibility.tier}</div>
                </div>

                {/* Accuracy */}
                <div className="min-w-0 bg-cream-100 dark:bg-white/5 rounded-xl p-4 text-center sm:text-left border border-cream-300 dark:border-white/10 hover:bg-cream-900/10 dark:hover:bg-white/10 transition">
                  <div className="text-3xl font-bold text-emerald-400">
                    {credibility.accuracyPct.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500 dark:text-slate-400 flex items-center justify-center sm:justify-start gap-1.5 min-w-0">
                    <span className="truncate">{t('myAccount.credibility.accuracy')}</span>
                    <InfoTooltip content={t('myAccount.credibility.accuracyTooltip', { hardWeight: credibility.hardPredictionWeight.toFixed(1), softWeight: credibility.softPredictionWeight.toFixed(1) })} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-500 mt-1 truncate">
                    {t('myAccount.credibility.correctOfTotal', { correct: credibility.correctPredictions, total: credibility.totalPredictions })}
                  </div>
                  {(credibility.hardPredictionsTotal > 0 || credibility.softPredictionsTotal > 0) && (
                    <div className="text-xs text-gray-500 dark:text-slate-500 mt-0.5 truncate" title={t('myAccount.credibility.hardSoftTooltip', { hardWeight: credibility.hardPredictionWeight.toFixed(1), softWeight: credibility.softPredictionWeight.toFixed(1) })}>
                      <span className="text-emerald-500 dark:text-emerald-400">{t('myAccount.credibility.hardCount', { count: credibility.hardPredictionsTotal })}</span>
                      {' · '}
                      <span className="text-amber-500 dark:text-amber-400">{t('myAccount.credibility.softCount', { count: credibility.softPredictionsTotal })}</span>
                    </div>
                  )}
                </div>

                {/* Combined Accuracy */}
                <div className="min-w-0 bg-cream-100 dark:bg-white/5 rounded-xl p-4 text-center sm:text-left border border-cream-300 dark:border-white/10 hover:bg-cream-900/10 dark:hover:bg-white/10 transition">
                  <div className="text-3xl font-bold text-purple-400">
                    {credibility.combinedAccuracy.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-slate-400 flex items-center justify-center sm:justify-start gap-1.5 min-w-0">
                    <span className="truncate">{t('myAccount.credibility.combinedAccuracy')}</span>
                    <InfoTooltip content={t('myAccount.credibility.combinedAccuracyTooltip')} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-500 mt-1 truncate">
                    {t('myAccount.credibility.bonus', { value: credibility.hardBonus.toFixed(1) })}
                  </div>
                </div>

                {/* Meta info */}
                <div className="min-w-0 bg-cream-100 dark:bg-white/5 rounded-xl p-4 text-center sm:text-left border border-cream-300 dark:border-white/10 hover:bg-cream-900/10 dark:hover:bg-white/10 transition">
                  <div className="text-sm text-gray-500 dark:text-slate-400 truncate">{t('myAccount.credibility.lastUpdated')}</div>
                  <div className="text-lg font-semibold text-gray-800 dark:text-slate-200">
                    {credibility?.calculatedAt
                      ? new Date(credibility.calculatedAt).toLocaleDateString()
                      : t('myAccount.credibility.realTime')}
                  </div>
                  {credibility.modelVersion && (
                    <div className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                      {t('myAccount.credibility.model', { version: credibility.modelVersion })}
                    </div>
                  )}
                </div>
              </div>

              {/* How is my score calculated? - collapsed by default so it doesn't
                  add noise to a card most visits just glance at. Shared with
                  GroupPostComposer.jsx -- see CredibilityExplainer.jsx. */}
              <CredibilityExplainer className="mt-4" />

              {/* Live guardrail-tier status -- computed from this user's own
                  real score/tier, never a hypothetical. See
                  GuardrailTierPanel.jsx / groupGuardrailTiers.js. This page
                  (MyAccount/profile.jsx) is always the logged-in user's own
                  account, not a public-viewer page, so no ownership gate
                  is needed here. */}
              <div className="mt-6">
                <GuardrailTierPanel statusTier={credibility?.tier} globalScore={credibility?.score} />
              </div>

              {/* Standalone Knowledge Assessment -- deliberately its own
                  panel, not merged into the credibility card above it. */}
              <div className="mt-4">
                <KnowledgeAssessmentSummary />
              </div>

              {/* Score History - why the score moved, not just what it is now */}
              {credHistory.length >= 2 && (() => {
                const w = 320, h = 56, pad = 4;
                const scores = credHistory.map((r) => r.score);
                const min = Math.min(...scores), max = Math.max(...scores);
                const range = max - min || 1;
                const points = credHistory
                  .map((r, i) => {
                    const x = pad + (i / (credHistory.length - 1)) * (w - pad * 2);
                    const y = h - pad - ((r.score - min) / range) * (h - pad * 2);
                    return `${x.toFixed(1)},${y.toFixed(1)}`;
                  })
                  .join(' ');
                const delta = credHistory[credHistory.length - 1].score - credHistory[0].score;
                const trendUp = delta >= 0;
                return (
                  <div className="mt-6 pt-6 border-t border-cream-300 dark:border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-slate-400">{t('myAccount.credibility.scoreHistory')}</h4>
                      <span className={`text-xs font-semibold ${trendUp ? 'text-emerald-400' : 'text-red-400'}`}>
                        {trendUp ? '+' : ''}{delta.toFixed(1)} {t('myAccount.credibility.sinceFirstTracked')}
                      </span>
                    </div>
                    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14" preserveAspectRatio="none">
                      <polyline
                        key={points}
                        points={points}
                        fill="none"
                        stroke={trendUp ? '#34d399' : '#f87171'}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="stocker-sparkline-draw-in"
                        pathLength="1"
                      />
                    </svg>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Professional Info Chips - Responsive layout */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-gray-500 dark:text-slate-400">
            {profileData.location && (
              <InfoChip 
                icon={MapPin} 
                text={profileData.location} 
                className="text-xs sm:text-sm"
              />
            )}
            {profileData.company && (
              <InfoChip 
                icon={Building} 
                text={profileData.company} 
                className="text-xs sm:text-sm"
              />
            )}
            {watchlistItems.length > 0 && (
              <InfoChip
                icon={Eye}
                text={t('myAccount.infoChips.assetsTracked', { count: watchlistItems.length })}
                className="text-xs sm:text-sm"
              />
            )}
            {earnedBadges.length > 0 && (
              <InfoChip
                icon={Award}
                text={t('myAccount.infoChips.achievementsCount', { count: earnedBadges.length })}
                className="text-xs sm:text-sm"
              />
            )}
          </div>

          {/* Bio Section - Professional formatting */}
          {profileData.bio && (
            <div className="bg-cream-100 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-cream-300 dark:border-white/10">
              <h4 className="text-sm font-semibold text-gray-500 dark:text-slate-400 mb-3 uppercase tracking-wide">
                {t('myAccount.professionalSummary')}
              </h4>
              <p className="text-gray-800 dark:text-slate-200 leading-relaxed text-sm sm:text-base">
                {profileData.bio}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

  const TabNavigation = () => (
    <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700/50 mb-8 overflow-hidden">
      <div className="flex overflow-x-auto scrollbar-hide sm:flex-wrap">
        {[
          { id: 'chips', icon: Shield, label: t('myAccount.tabs.chips') },
          { id: 'connections', icon: Users, label: t('myAccount.tabs.connections') },
          { id: 'achievements', icon: Trophy, label: t('myAccount.tabs.achievements'), badge: earnedBadges.length },
          { id: 'analytics', icon: BarChart3, label: t('myAccount.tabs.analytics') },
          { id: 'profile', icon: User, label: t('myAccount.tabs.profile') },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              handleTabChange(tab.id);
              // Both leaderboards (Credibility + Activity) live in this tab --
              // the only place either is reachable today.
              if (tab.id === 'achievements') logAnalyticsEvent('leaderboard_viewed');
            }}
            aria-label={tab.label}
            aria-current={activeTab === tab.id ? 'page' : undefined}
            title={tab.label}
            className={`flex items-center justify-center sm:justify-start space-x-0 sm:space-x-2
              flex-shrink-0 px-3.5 py-3 sm:px-6 sm:py-4 lg:px-8 lg:py-5
              font-medium relative transition-all
              focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-inset ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
          >
            <tab.icon className="w-5 h-5" />
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.badge>0 && (
              <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full ml-1.5 sm:ml-2">
                {tab.badge}
              </span>
            )}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-purple-400" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
  
  const level = Math.floor(points.total / 100) + 1;
  const progress = points.total % 100 === 0 && points.total > 0 ? 100 : points.total % 100;

  const AchievementsSection = () => (

    <div className="space-y-8">
      {/* Same md: -> lg: fix as the header stats grid above, same sidebar-
          width reasoning (2026-08-11). */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <StatCard
          value={points.total}
          label={t('myAccount.achievementsSection.todayPoints', { count: points.daily })}
          icon={TrendingUp}
          color="text-blue-200"
          bg="bg-gradient-to-br from-blue-600 to-blue-800 text-white"
        />
        <StatCard
          value={level}
          label={t('myAccount.achievementsSection.xpProgress', { progress })}
          icon={Crown}
          color="text-purple-200"
          bg="bg-gradient-to-br from-purple-600 to-purple-800 text-white"
        />
        <StatCard
          value={earnedBadges.length}
          label={t('myAccount.achievementsSection.inProgress', { count: progressBadges.length })}
          icon={Medal}
          color="text-green-200"
          bg="bg-gradient-to-br from-green-600 to-green-800 text-white"
        />
        <StatCard
          value={points.streak}
          label={t('myAccount.achievementsSection.daysActive')}
          icon={Flame}
          color="text-orange-200"
          bg="bg-gradient-to-br from-orange-600 to-orange-800 text-white"
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 -mt-4">
        {t('myAccount.achievementsSection.activityDisclaimer')}
      </p>

      {credLeaderboard.length > 0 && (
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3 sm:gap-4">
            <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-blue-500 flex-shrink-0" />
            {t('myAccount.achievementsSection.credLeaderboardTitle')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 sm:mb-8">
            {t('myAccount.achievementsSection.credLeaderboardSubtitle')}
          </p>
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 overflow-hidden">
            <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
              {credLeaderboard.slice(0, 10).map((player, index) => (
                <div
                  key={player.userId || `cred-leaderboard-${index}`}
                  className={`flex items-center justify-between gap-3 p-3 sm:p-4 rounded-xl transition-all ${
                    player.userId === userId
                      ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-2 border-blue-500/50'
                      : 'bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div
                      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                        index === 0
                          ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
                          : index === 1
                          ? 'bg-gradient-to-r from-gray-400 to-gray-600 text-white'
                          : index === 2
                          ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
                          : 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {index === 0 ? '🏆' : index + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white truncate">
                        {player.username}
                        {player.userId === userId && (
                          <span className="text-blue-400 ml-2">{t('myAccount.achievementsSection.you')}</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {player.tier} · {(player.accuracy * 100).toFixed(0)}{t('myAccount.achievementsSection.accuracySuffix')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-base sm:text-lg text-blue-400 whitespace-nowrap">{player.score.toFixed(1)}</div>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('myAccount.achievementsSection.score')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {earnedBadges.length > 0 ? (
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="flex items-center gap-3 sm:gap-4">
              <Trophy className="w-7 h-7 sm:w-8 sm:h-8 text-yellow-500 flex-shrink-0" />
              {t('myAccount.achievementsSection.earnedTitle')}
            </span>
            <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-3 py-1 rounded-full text-base sm:text-lg font-bold">
              {earnedBadges.length}
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {earnedBadges.map((badge, index) => (
              <BadgeCard
                key={badge.id || `earned-${index}`}
                badge={{ ...badge, progress: badge.total }}
                isEarned={true}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Trophy className="w-16 h-16 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('myAccount.achievementsSection.noAchievements')}</p>
          <p className="text-gray-500 text-sm mt-2">{t('myAccount.achievementsSection.noAchievementsHint')}</p>
        </div>
      )}

      {progressBadges.length > 0 && (
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="flex items-center gap-3 sm:gap-4">
              <Target className="w-7 h-7 sm:w-8 sm:h-8 text-blue-500 flex-shrink-0" />
              {t('myAccount.achievementsSection.availableTitle')}
            </span>
            <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1 rounded-full text-base sm:text-lg font-bold">
              {progressBadges.length}
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {progressBadges.map((badge, index) => (
              <BadgeCard
                key={badge.id || `progress-${index}`}
                badge={{
                  ...badge,
                  progress: Math.max(0, badge.progress || 0),
                  total: badge.total > 0 ? badge.total : 1,
                }}
                isEarned={false}
              />
            ))}
          </div>
        </div>
      )}

      {leaderboard.length > 0 && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-3 sm:gap-4">
            <Crown className="w-6 h-6 sm:w-7 sm:h-7 text-purple-400 flex-shrink-0" />
            {t('myAccount.achievementsSection.activityLeaderboardTitle')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 sm:mb-8">
            {t('myAccount.achievementsSection.activityLeaderboardSubtitle')}
          </p>
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 overflow-hidden">
            <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
              {leaderboard.slice(0, 10).map((player, index) => (
                <div
                  key={player.userId || `leaderboard-${index}`}
                  className={`flex items-center justify-between gap-3 p-3 sm:p-4 rounded-xl transition-all ${
                    player.userId === userId
                      ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-2 border-blue-500/50'
                      : 'bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div
                      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                        index === 0
                          ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
                          : index === 1
                          ? 'bg-gradient-to-r from-gray-400 to-gray-600 text-white'
                          : index === 2
                          ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
                          : 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {index === 0 ? '🏆' : index + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white truncate">
                        {player.username}
                        {player.userId === userId && (
                          <span className="text-blue-400 ml-2">{t('myAccount.achievementsSection.you')}</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t('myAccount.achievementsSection.levelLabel', { level: Math.floor(player.total / 100) + 1 })}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-base sm:text-lg text-blue-400 whitespace-nowrap">{player.total} {t('myAccount.achievementsSection.xpSuffix')}</div>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{player.badgesCount} {t('myAccount.achievementsSection.badgesSuffix')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const AnalyticsSection = () => (
    <div className="space-y-6 sm:space-y-8">
      <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-6 lg:p-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="flex items-center gap-3">
            <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400 flex-shrink-0" />
            {t('myAccount.analyticsSection.credibilityAnalyticsTitle')}
          </span>
          {credibilityStatus === 'pending-202' && (
            <div className="flex items-center text-yellow-400 text-sm">
              <Timer className="w-4 h-4 mr-1 animate-spin" />
              {t('myAccount.analyticsSection.computing')}
            </div>
          )}
        </h2>

        {credibility ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <StatCard
              value={credibility.score.toFixed(1)}
              label={t('myAccount.analyticsSection.tierLabel', { tier: credibility.tier })}
              subtitle={t('myAccount.analyticsSection.tierSubtitle')}
              icon={Shield}
              color="text-blue-400"
              bg="bg-gradient-to-br from-blue-600/20 to-blue-800/20"
            />
            <StatCard
              value={`${credibility.accuracyPct.toFixed(1)}%`}
              label={t('myAccount.analyticsSection.transparencyLabel', { value: credibility.transparency })}
              icon={Target}
              color="text-green-400"
              bg="bg-gradient-to-br from-green-600/20 to-green-800/20"
            />
            <StatCard
              value={credibility.consistency.toFixed(2)}
              label={t('myAccount.analyticsSection.influenceLabel', { value: credibility.influence.toFixed(2) })}
              icon={Activity}
              color="text-purple-400"
              bg="bg-gradient-to-br from-purple-600/20 to-purple-800/20"
            />
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">{t('myAccount.analyticsSection.noCredData')}</p>
            <button
              onClick={handleCredibilityRecompute}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
            >
              {t('myAccount.analyticsSection.calculateButton')}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-6 lg:p-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-3">
          <Eye className="w-6 h-6 sm:w-7 sm:h-7 text-green-400 flex-shrink-0" />
          {t('myAccount.analyticsSection.watchlistAnalyticsTitle')}
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <StatCard
            value={watchlistTotal}
            label={t('myAccount.analyticsSection.totalStocks')}
            icon={Eye}
            color="text-green-400"
            bg="bg-gradient-to-br from-green-600/20 to-green-800/20"
          />
          <StatCard
            value="—"
            label={t('myAccount.analyticsSection.performanceFuture')}
            icon={TrendingUp}
            color="text-blue-400"
            bg="bg-gradient-to-br from-blue-600/20 to-blue-800/20"
          />
          <StatCard
            value="—"
            label={t('myAccount.analyticsSection.analyticsFuture')}
            icon={BarChart3}
            color="text-purple-400"
            bg="bg-gradient-to-br from-purple-600/20 to-purple-800/20"
          />
          <StatCard
            value="—"
            label={t('myAccount.analyticsSection.alertsFuture')}
            icon={AlertCircle}
            color="text-orange-400"
            bg="bg-gradient-to-br from-orange-600/20 to-orange-800/20"
          />
        </div>

        {watchlistItems.length > 0 && (
          <div className="mt-6 sm:mt-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('myAccount.analyticsSection.recentAdditions')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {watchlistItems.slice(0, 6).map((item, index) => (
                <div
                  key={item.id || item.symbol || `watchlist-${index}`}
                  className="bg-gray-100 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-300 dark:border-gray-600/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors min-w-0"
                >
                  <div className="font-semibold text-gray-900 dark:text-white truncate">{item.symbol || t('myAccount.analyticsSection.unknown')}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {item.name || item.company_name || t('myAccount.analyticsSection.noDescription')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-6 lg:p-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-3">
          <Activity className="w-6 h-6 sm:w-7 sm:h-7 text-purple-400 flex-shrink-0" />
          {t('myAccount.analyticsSection.recentActivityTitle')}
        </h2>

        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-100 dark:bg-gray-700/30 rounded-xl">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gray-900 dark:text-white font-medium truncate">{t('myAccount.analyticsSection.pointsUpdate')}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{t('myAccount.analyticsSection.totalXp', { count: points.total })}</div>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">{t('myAccount.analyticsSection.today')}</div>
          </div>

          {earnedBadges.length > 0 && (
            <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-100 dark:bg-gray-700/30 rounded-xl">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Medal className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 dark:text-white font-medium truncate">{t('myAccount.analyticsSection.badgeEarned')}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {t('myAccount.analyticsSection.latestBadge', { name: earnedBadges[earnedBadges.length - 1]?.name || t('myAccount.analyticsSection.unknownBadge') })}
                </div>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0 whitespace-nowrap">
                {earnedBadges[earnedBadges.length - 1]?.earnedAt
                  ? new Date(earnedBadges[earnedBadges.length - 1].earnedAt).toLocaleDateString()
                  : t('myAccount.analyticsSection.recently')}
              </div>
            </div>
          )}

          {watchlistItems.length > 0 && (
            <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-100 dark:bg-gray-700/30 rounded-xl">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Eye className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 dark:text-white font-medium truncate">{t('myAccount.analyticsSection.watchlistUpdated')}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {t('myAccount.analyticsSection.followingStocks', { count: watchlistItems.length })}
                </div>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">{t('myAccount.analyticsSection.recently')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

const ProfileSection = ({ title, fields, icon: Icon }) => (
  <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
    <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
      <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-2xl border border-blue-500/30 flex-shrink-0">
        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400" />
      </div>
      <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white truncate">{title}</h2>
    </div>

    <div className="grid gap-4 sm:gap-6 mb-6 sm:mb-8">
      {fields.map((field) => {
        const FieldIcon = getFieldIcon(field);
        const isTextarea = [
          'bio',
          'education',
          'skills',
          'experience',
          'social_media_links',
          'interests',
        ].includes(field);
        const inputType = field.includes('email')
          ? 'email'
          : field === 'website'
          ? 'url'
          : field === 'date_of_birth'
          ? 'date'
          : 'text';

        return (
          <div
            key={field}
            className="flex flex-col sm:flex-row sm:items-start
                      gap-4 p-4 sm:p-6 rounded-2xl
                      hover:bg-gray-100 dark:hover:bg-gray-700/30 transition-colors"
          >
            <div className="p-3 bg-gray-100 dark:bg-gray-700/50 rounded-xl flex-shrink-0">
              <FieldIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                {formatFieldName(field)}
              </p>
              {editMode ? (
                isTextarea ? (
                  <textarea
                    name={field}
                    value={profileData[field] || ''}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-4 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white resize-none transition-colors"
                    rows="4"
                    placeholder={t('myAccount.enterYourField', { field: formatFieldName(field).toLowerCase() })}
                  />
                ) : (
                  <input
                    type={inputType}
                    name={field}
                    value={profileData[field] || ''}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-4 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white transition-colors"
                    placeholder={t('myAccount.enterYourField', { field: formatFieldName(field).toLowerCase() })}
                  />
                )
              ) : (
                <p className="text-gray-900 dark:text-white text-lg leading-relaxed break-words">
                  {profileData[field] ? (
                    profileData[field]
                  ) : (
                    <span className="text-gray-500 italic">{t('myAccount.notProvided')}</span>
                  )}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>

    {/* Action Buttons now BELOW the profile fields */}
    <div className="flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4 mt-6">
      <button
        onClick={() => setEditMode(!editMode)}
        className="flex items-center justify-center space-x-2 w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
      >
        <Edit3 className="w-5 h-5" />
        <span>{editMode ? t('myAccount.cancel') : t('myAccount.editProfile')}</span>
      </button>

      <button
        onClick={() => setActiveSection('password')}
        className="flex items-center justify-center space-x-2 w-full sm:w-auto px-6 py-3 bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 rounded-xl border border-gray-300 dark:border-gray-600/50 hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
      >
        <Lock className="w-5 h-5" />
        <span>{t('myAccount.security')}</span>
      </button>
    </div>
  </div>
);


  const PasswordModal = () => (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm
                flex items-end sm:items-center justify-center
                p-4 z-50">
      <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md rounded-3xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-700/50 max-h-[90vh] overflow-y-auto">
        <div className="p-5 sm:p-8">
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">{t('myAccount.passwordModal.title')}</h2>
            <button
              onClick={() => setActiveSection(null)}
              aria-label={t('myAccount.passwordModal.close')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-5 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">{t('myAccount.passwordModal.currentPassword')}</label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-4 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white transition-colors"
                required
                placeholder={t('myAccount.passwordModal.currentPasswordPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">{t('myAccount.passwordModal.newPassword')}</label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-4 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white transition-colors"
                required
                placeholder={t('myAccount.passwordModal.newPasswordPlaceholder')}
                minLength="8"
              />
            </div>
            <button
              type="submit"
              disabled={!passwordData.currentPassword || !passwordData.newPassword || passwordChanging}
              className="w-full flex items-center justify-center space-x-3 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800"
            >
              <Save className="w-5 h-5" />
              <span>{passwordChanging ? t('myAccount.saving') : t('myAccount.passwordModal.updateButton')}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  const ErrorDisplay = () => {
    const errors = [profileError, gamificationError].filter(Boolean);
    if (errors.length === 0) return null;
    
    return (
      <div className="bg-red-900/50 border-l-4 border-red-500 p-6 rounded-xl mb-8">
        <div className="flex items-center">
          <AlertCircle className="w-6 h-6 text-red-400 mr-3" />
          <div className="ml-3">
            <h3 className="text-red-200 font-medium mb-2">{t('myAccount.errorOccurred')}</h3>
            {errors.map((error, index) => (
              <p key={index} className="text-red-200 text-sm">
                {typeof error === 'object' ? JSON.stringify(error) : String(error)}
              </p>
            ))}
          </div>
        </div>
      </div>
    );
  };


// ─────────────────────────────────────────────
// AUTH GUARD (DO NOT REMOVE)
// ─────────────────────────────────────────────
if (!user && !loading) {
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8">
      <div className="text-center py-16 sm:py-20">
        <AlertCircle className="w-14 h-14 sm:w-16 sm:h-16 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('myAccount.profileNotFound')}</h1>
        <p className="text-gray-500 dark:text-gray-400">{t('myAccount.pleaseLogIn')}</p>
      </div>
    </div>
  );
}

  // ─────────────────────────────────────────────
  // MAIN PAGE RENDER
  // Scrolling is owned entirely by <main> in AppLayout.jsx (overflow-y-auto).
  // This page must not fight that with its own min-h-screen/overflow-hidden --
  // that combination silently broke vertical scrolling here, since it created
  // a second, competing (and non-scrolling) height context nested inside the
  // real scroll owner. Match the working pattern used by dashboard.jsx.
  // ─────────────────────────────────────────────
  return (
    <div className="flex flex-col flex-1 bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900 w-full overflow-x-hidden">
      {newBadgeToast && (
        <BadgeEarnedToast badge={newBadgeToast} onDismiss={() => setNewBadgeToast(null)} />
      )}
      {tierUpToast && (
        <TierUpToast tier={tierUpToast} onDismiss={() => setTierUpToast(null)} />
      )}
      <div className="flex flex-grow w-full">

        <div className="flex-1 w-full overflow-x-hidden">

          {/* ✅ CONTENT CONTAINER */}
          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 text-gray-900 dark:text-slate-100">

            {loading && (
              <div className="flex items-center justify-center py-20">
                <StockerEyeLoader size={64} />
              </div>
            )}

            <ErrorDisplay />

            {user && (
              <>
                <ProfileHeader />
                <TabNavigation />

                {activeTab === 'profile' && (
                  <>
                    <ProfileSection
                      title={t('myAccount.sections.personalInfo')}
                      fields={['email', 'phone_number', 'address', 'date_of_birth', 'backup_email']}
                      icon={User}
                    />

                    <ProfileSection
                      title={t('myAccount.sections.professionalDetails')}
                      fields={['professional_title', 'company', 'bio']}
                      icon={Briefcase}
                    />

                    <ProfileSection
                      title={t('myAccount.sections.educationSkills')}
                      fields={['education', 'skills', 'experience']}
                      icon={GraduationCap}
                    />

                    <ProfileSection
                      title={t('myAccount.sections.onlinePresence')}
                      fields={['website', 'social_media_links']}
                      icon={Globe}
                    />

                    <ProfileSection
                      title={t('myAccount.sections.interests')}
                      fields={['interests']}
                      icon={Star}
                    />

                    {editMode && (
                      <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                          <button
                            onClick={handleProfileSubmit}
                            disabled={saving}
                            className="flex items-center justify-center space-x-3 px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Save className="w-5 h-5" />
                            <span>{saving ? t('myAccount.saving') : t('myAccount.saveChanges')}</span>
                          </button>

                          <button
                            onClick={() => setEditMode(false)}
                            disabled={saving}
                            className="flex items-center justify-center space-x-3 px-8 py-4 bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 rounded-xl border border-gray-300 dark:border-gray-600/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <X className="w-5 h-5" />
                            <span>{t('myAccount.cancel')}</span>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-6 lg:p-8">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
                        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
                          {t('myAccount.accountActions')}
                        </h2>

                        <button
                          onClick={handleLogout}
                          className="flex items-center justify-center space-x-3 w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                        >
                          <LogOut className="w-5 h-5" />
                          <span>{t('myAccount.logout')}</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'achievements' && <AchievementsSection />}
                {activeTab === 'analytics' && <AnalyticsSection />}
                {activeTab === 'connections' && (
                  <ConnectionsTab userId={user?.id} isOwner />
                )}
                {activeTab === 'chips' && (
                  <CredibilityChipsTab userId={user?.id} isOwner />
                )}
              </>
            )}

            {activeSection === 'password' && <PasswordModal />}

          </div>
        </div>
      </div>
    </div>
  );


};

export default ProfilePage;